import { FunctionComponent } from 'react'
import loadable, { LoadableComponent } from '@loadable/component'

// Types
import type { Location } from 'history'

// Utils
import { UserIsAuthenticatedOrShowLoginForModal } from 'authenticated'

// Loadable
const UserSongUpdateModal = loadable(() => import('components/usersong/guides'))

type ModalProps = {
  onClose?: () => void
}

type ModalObj = Record<
  string,
  {
    title?: string
    size: 'SMALL' | 'MEDIUM' | 'LARGE' | 'XXL'
    mobileFullscreen?: boolean
    mobileAlignment?: 'TOP' | 'CENTER'
    noCloseOnEscAndBackdrop?: boolean
    transitionEnabled?: boolean
    component: LoadableComponent<{ onClose: () => void; location?: Location; fallback?: JSX.Element }> | FunctionComponent<ModalProps>
    noPadding?: boolean
  }
>

export const MODALS = (queryObj: { loop: string; url: string; collectionId: string }): ModalObj => {
  const { loop, url, collectionId } = queryObj
  const shareModalIsLoop = url && url.includes('shared_loop')
  return {
    addemail: {
      title: 'Add email',
      size: 'SMALL',
      mobileFullscreen: false,
      component: loadable(() => import('components/profile/addemail')),
    },
    addtrack: {
      size: 'SMALL',
      title: `Add or remove ${loop ? 'loop' : 'track'} from collection`,
      mobileFullscreen: true,
      component: UserIsAuthenticatedOrShowLoginForModal(
        loadable(() => import('components/collection/addtrackmodal')) as FunctionComponent<ModalProps>,
      ),
    },
    cancelsubscription: {
      size: 'MEDIUM',
      mobileFullscreen: true,
      component: UserIsAuthenticatedOrShowLoginForModal(
        loadable(() => import('components/profile/cancelsubscriptionmodal')) as FunctionComponent<ModalProps>,
      ),
    },
    collection: {
      title: `${collectionId ? 'Edit' : 'Create new'} collection`,
      size: 'SMALL',
      mobileFullscreen: true,
      mobileAlignment: 'TOP',
      component: UserIsAuthenticatedOrShowLoginForModal(
        loadable(() => import('components/collection/collectionmodal')) as FunctionComponent<ModalProps>,
      ),
    },
    changeplan: {
      title: 'Change plan',
      size: 'MEDIUM',
      mobileFullscreen: true,
      component: UserIsAuthenticatedOrShowLoginForModal(
        loadable(() => import('components/profile/changeplanmodal')) as FunctionComponent<ModalProps>,
      ),
    },
    createversion: {
      size: 'MEDIUM',
      mobileFullscreen: true,
      mobileAlignment: 'CENTER',
      component: loadable(() => import('components/usersong/createversionmodal')),
    },
    credits: {
      size: 'LARGE',
      mobileFullscreen: true,
      mobileAlignment: 'TOP',
      component: UserIsAuthenticatedOrShowLoginForModal(
        loadable(() => import('components/base/creditsmodal')) as FunctionComponent<ModalProps>,
      ),
    },
    deleteaccount: {
      title: 'Delete account',
      size: 'MEDIUM',
      mobileFullscreen: true,
      mobileAlignment: 'TOP',
      component: loadable(() => import('components/profile/deleteaccountmodal')),
    },
    deleteorcancel: {
      title: 'Cancel subscription or delete account?',
      size: 'LARGE',
      mobileFullscreen: true,
      mobileAlignment: 'TOP',
      component: loadable(() => import('components/profile/deleteorcancelmodal')),
    },
    download: {
      title: 'Download track',
      size: 'SMALL',
      mobileFullscreen: true,
      component: loadable(() => import('components/base/downloadmodal')),
    },
    freeplan: {
      title: 'About the limited plan',
      size: 'SMALL',
      mobileFullscreen: true,
      component: loadable(() => import('components/subscribe/freeplanmodal')),
    },
    invite: {
      title: 'Copy invite link',
      size: 'MEDIUM',
      component: loadable(() => import('components/base/invitemodal')),
    },
    login: {
      size: 'SMALL',
      mobileFullscreen: true,
      component: loadable(() => import('components/nologin/login')),
    },
    newsletter: {
      size: 'LARGE',
      mobileFullscreen: false,
      component: loadable(() => import('components/base/newsletter/newslettermodal')),
      noPadding: true,
    },
    newsletterconfirmation: {
      title: 'Thanks for subscribing!',
      size: 'SMALL',
      component: loadable(() => import('components/base/newsletter/newsletterconfirmationmodal')),
    },
    paymentmethod: {
      title: 'Update payment details',
      size: 'MEDIUM',
      mobileFullscreen: true,
      mobileAlignment: 'TOP',
      component: loadable(() => import('components/profile/paymentmethodmodal')),
    },
    previewlicense: {
      size: 'MEDIUM',
      mobileFullscreen: true,
      mobileAlignment: 'TOP',
      component: loadable(() => import('components/usersong/previewlicensemodal')),
    },
    receipts: {
      title: 'Receipts',
      size: 'XXL',
      mobileFullscreen: true,
      mobileAlignment: 'TOP',
      component: loadable(() => import('components/profile/receiptsmodal')),
    },
    registerrecording: {
      size: 'LARGE',
      noCloseOnEscAndBackdrop: true,
      mobileFullscreen: true,
      component: UserSongUpdateModal,
    },
    releasesong: {
      size: 'LARGE',
      noCloseOnEscAndBackdrop: true,
      mobileFullscreen: true,
      component: UserSongUpdateModal,
    },
    resetpassword: {
      title: 'Reset password',
      size: 'SMALL',
      component: loadable(() => import('components/nologin/resetpassword')),
    },
    revenuereport: {
      size: 'LARGE',
      noCloseOnEscAndBackdrop: true,
      component: loadable(() => import('components/usersong/accounting/revenuereportmodal')),
    },
    rightsholders: {
      size: 'LARGE',
      noCloseOnEscAndBackdrop: true,
      component: UserSongUpdateModal,
    },
    share: {
      title: `Share${shareModalIsLoop ? ' loop' : ''}`,
      size: 'MEDIUM',
      component: loadable(() => import('components/base/sharemodal')),
    },
    shortcuts: {
      title: 'Keyboard Shortcuts',
      size: 'MEDIUM',
      component: loadable(() => import('components/base/keyboardshortcutsmodal')),
    },
    signup: {
      size: 'SMALL',
      noCloseOnEscAndBackdrop: true,
      mobileFullscreen: true,
      component: loadable(() => import('components/nologin/signup/signupmodal'), { ssr: false }),
    },
    socialconfirm: {
      title: 'Sign up with Facebook',
      size: 'MEDIUM',
      component: loadable(() => import('components/nologin/signup/socialconfirm')),
    },
    songinfo: {
      size: 'LARGE',
      noCloseOnEscAndBackdrop: true,
      component: UserSongUpdateModal,
      mobileFullscreen: true,
    },
    starttrial: {
      size: 'LARGE',
      mobileFullscreen: true,
      component: loadable(() => import('components/base/starttrialmodal')),
    },
    terms: {
      title: 'Updated terms',
      size: 'MEDIUM',
      mobileFullscreen: true,
      component: loadable(() => import('components/staticpages/termsmodal')),
    },
    usersong: {
      size: 'LARGE',
      noCloseOnEscAndBackdrop: true,
      mobileFullscreen: true,
      component: loadable(() => import('components/usersongs/usersongmodal')),
    },
    usersongreceive: {
      size: 'LARGE',
      mobileFullscreen: true,
      component: loadable(() => import('components/usersong/receivesongmodal')),
    },
    usersongsend: {
      size: 'LARGE',
      mobileFullscreen: true,
      component: loadable(() => import('components/usersong/sendsongmodal')),
    },
    verification: {
      title: 'Please verify your email',
      size: 'MEDIUM',
      component: loadable(() => import('components/profile/verificationmodal')),
    },
    verificationresponse: {
      title: 'Email address verified!',
      size: 'SMALL',
      component: loadable(() => import('components/profile/verificationresponsemodal')),
    },
    verificationresponsefail: {
      title: 'Something went wrong!',
      size: 'SMALL',
      component: loadable(() => import('components/profile/verificationresponsemodalfail')),
    },
    worksregistration: {
      size: 'LARGE',
      noCloseOnEscAndBackdrop: true,
      mobileFullscreen: true,
      component: UserSongUpdateModal,
    },
  }
}
